import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CHAT_MESSAGE_SENT_EVENT,
  CHAT_WALKTHROUGH_SET_INPUT_EVENT,
  CHAT_WALKTHROUGH_SET_MODE_EVENT,
  CHAT_WALKTHROUGH_SET_WIDTH_EVENT,
  CHAT_WALKTHROUGH_SET_TAGGED_AGENTS_EVENT,
  CHAT_WALKTHROUGH_CLEAR_TAGGED_AGENTS_EVENT,
  dispatchWalkthroughState,
  isWalkthroughCompleted,
  setWalkthroughCompleted,
} from "./walkthroughState";
import { isDemoLotActive } from "../DemoLotBanner/demoLotMode";
import { WALKTHROUGH_STEPS, WalkthroughStep, StepSideEffect } from "./steps";

const AGENT_ACTION_APPLIED_EVENT = "agent-action-applied";

type WalkthroughContextValue = {
  active: boolean;
  step: WalkthroughStep | null;
  stepIndex: number;
  totalSteps: number;
  isIdle: boolean;
  start: () => void;
  end: (markComplete: boolean) => void;
  next: () => void;
};

const WalkthroughContext = createContext<WalkthroughContextValue | null>(null);

const applyEffects = (effects?: StepSideEffect[]) => {
  if (!effects) return;
  for (const effect of effects) {
    if (effect.type === "set-chat-width") {
      window.dispatchEvent(
        new CustomEvent(CHAT_WALKTHROUGH_SET_WIDTH_EVENT, {
          detail: { width: effect.width },
        })
      );
    } else if (effect.type === "set-chat-input") {
      window.dispatchEvent(
        new CustomEvent(CHAT_WALKTHROUGH_SET_INPUT_EVENT, {
          detail: { value: effect.value },
        })
      );
    } else if (effect.type === "set-chat-mode") {
      window.dispatchEvent(
        new CustomEvent(CHAT_WALKTHROUGH_SET_MODE_EVENT, {
          detail: { mode: effect.mode },
        })
      );
    } else if (effect.type === "set-chat-tagged-agents") {
      window.dispatchEvent(
        new CustomEvent(CHAT_WALKTHROUGH_SET_TAGGED_AGENTS_EVENT, {
          detail: { agentKeys: effect.agentKeys },
        })
      );
    } else if (effect.type === "clear-chat-tagged-agents") {
      window.dispatchEvent(
        new CustomEvent(CHAT_WALKTHROUGH_CLEAR_TAGGED_AGENTS_EVENT, {})
      );
    } else if (effect.type === "enable-advanced-pricing") {
      window.dispatchEvent(new CustomEvent("walkthrough-enable-advanced", {}));
    } else if (effect.type === "enable-registry") {
      window.dispatchEvent(new CustomEvent("walkthrough-enable-registry", {}));
    }
  }
};

export const WalkthroughProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [active, setActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const autoStartedRef = useRef(false);

  const step = active ? WALKTHROUGH_STEPS[stepIndex] || null : null;

  useEffect(() => {
    if (typeof document === "undefined") return;
    if (active) {
      document.body.classList.add("walkthrough-active");
    } else {
      document.body.classList.remove("walkthrough-active");
    }
    return () => {
      document.body.classList.remove("walkthrough-active");
    };
  }, [active]);

  // Track current step on body via data attribute for CSS targeting
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (active && step) {
      document.body.setAttribute("data-walkthrough-step", step.id);
    } else {
      document.body.removeAttribute("data-walkthrough-step");
    }
    return () => {
      document.body.removeAttribute("data-walkthrough-step");
    };
  }, [active, step]);

  const end = useCallback((markComplete: boolean) => {
    setActive((prev) => {
      if (!prev) return prev;
      if (markComplete) {
        setWalkthroughCompleted(true);
      }
      // Restore chat width to default on end
      window.dispatchEvent(
        new CustomEvent(CHAT_WALKTHROUGH_SET_WIDTH_EVENT, {
          detail: { width: "restore" },
        })
      );
      dispatchWalkthroughState(false);
      return false;
    });
    setStepIndex(0);
  }, []);

  const start = useCallback(() => {
    setWalkthroughCompleted(false);
    setStepIndex(0);
    setActive(true);
    dispatchWalkthroughState(true);
    // Land on the revenue dashboard tab in day mode for a consistent starting point.
    const lotMatch = location.pathname.match(/^\/lot\/([^/]+)/);
    if (lotMatch) {
      const lotId = lotMatch[1];
      const revenuePath = `/lot/${lotId}/dashboard/revenue`;
      localStorage.setItem("lastDashboardTab", "revenue");
      if (!location.pathname.startsWith(revenuePath)) {
        navigate(revenuePath);
      }
    }
    // Ensure AI chat starts closed for a clean initial state
    window.dispatchEvent(
      new CustomEvent(CHAT_WALKTHROUGH_SET_WIDTH_EVENT, {
        detail: { width: "closed" },
      })
    );
    // Delay slightly so DashboardHeader has mounted and registered its event listener
    window.setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("walkthrough-set-timeframe", { detail: { timeframe: "day" } })
      );
    }, 150);
    queueMicrotask(() => {
      applyEffects(WALKTHROUGH_STEPS[0]?.onEnter);
    });
  }, [location.pathname, navigate]);

  const next = useCallback(() => {
    setStepIndex((prev) => {
      const nextIndex = prev + 1;
      if (nextIndex >= WALKTHROUGH_STEPS.length) {
        // Final step finished — complete tour
        setWalkthroughCompleted(true);
        setActive(false);
        dispatchWalkthroughState(false);
        window.dispatchEvent(
          new CustomEvent(CHAT_WALKTHROUGH_SET_WIDTH_EVENT, {
            detail: { width: "restore" },
          })
        );
        return 0;
      }
      // Apply next step's onEnter effects
      const nextStep = WALKTHROUGH_STEPS[nextIndex];
      queueMicrotask(() => applyEffects(nextStep?.onEnter));
      return nextIndex;
    });
  }, []);

  // Auto-start: demo mode + not completed + on a dashboard route.
  useEffect(() => {
    if (autoStartedRef.current) return;
    if (active) return;
    // Once we've auto-started or skipped this session, never auto-start again
    // even if the WalkthroughProvider remounts (e.g. on lot switch).
    if (typeof sessionStorage !== "undefined") {
      if (sessionStorage.getItem("walkthrough_session_handled") === "true") {
        autoStartedRef.current = true;
        return;
      }
    }
    if (!isDemoLotActive()) return;
    if (isWalkthroughCompleted()) {
      autoStartedRef.current = true;
      try { sessionStorage.setItem("walkthrough_session_handled", "true"); } catch {}
      return;
    }
    if (!/^\/lot\/[^/]+\/dashboard/.test(location.pathname)) return;
    autoStartedRef.current = true;
    try { sessionStorage.setItem("walkthrough_session_handled", "true"); } catch {}
    // Defer slightly so target elements have mounted.
    const timer = window.setTimeout(() => {
      start();
    }, 600);
    return () => window.clearTimeout(timer);
  }, [location.pathname, start, active]);

  // Advance triggers reactive to events / route changes.
  useEffect(() => {
    if (!active || !step) return;

    if (step.advance.type === "route-change") {
      const suffix = step.advance.pathSuffix;
      if (location.pathname.endsWith(suffix)) {
        next();
      }
      return;
    }

    if (step.advance.type === "send-message") {
      const handler = () => next();
      window.addEventListener(CHAT_MESSAGE_SENT_EVENT, handler);
      return () => window.removeEventListener(CHAT_MESSAGE_SENT_EVENT, handler);
    }

    if (step.advance.type === "custom-event") {
      const eventName = step.advance.eventName;
      const handler = () => next();
      window.addEventListener(eventName, handler);
      return () => window.removeEventListener(eventName, handler);
    }

    if (step.advance.type === "agent-applied") {
      const handler = () => next();
      window.addEventListener(AGENT_ACTION_APPLIED_EVENT, handler);
      window.addEventListener("ai-chat-agent-error", handler);
      return () => {
        window.removeEventListener(AGENT_ACTION_APPLIED_EVENT, handler);
        window.removeEventListener("ai-chat-agent-error", handler);
      };
    }
    return;
  }, [active, step, location.pathname, next]);

  // Global skip/advance via Enter key
  useEffect(() => {
    if (!active) return;
    const keyHandler = (e: KeyboardEvent) => {
      // Don't advance if the user is typing in a textarea or input (like AI chat)
      // unless we specifically want it to?
      // Actually, if they are hardlocked, they need a way out. 
      // But we should be careful not to skip if they just press enter to send a message.
      // However, most steps don't have an input.
      const target = e.target as HTMLElement;
      if (target.tagName === "TEXTAREA" || target.tagName === "INPUT") {
        // If it's a send-message step, Enter is used to send. 
        // If it's agent-applied, they might be typing a value.
        // Let's only allow Enter advance if NOT in an input, OR if it's a specific step type.
        return;
      }
      if (e.key === "Enter") {
        next();
      }
    };
    window.addEventListener("keydown", keyHandler);
    return () => window.removeEventListener("keydown", keyHandler);
  }, [active, next]);

  const [isIdle, setIsIdle] = useState(false);

  // Idle timer: if no activity for 15s during walkthrough, show the skip toast.
  // Once shown it stays until the user clicks it or advances to the next step.
  useEffect(() => {
    setIsIdle(false);
    if (!active) return;
    const timer = window.setTimeout(() => setIsIdle(true), 15000);
    return () => window.clearTimeout(timer);
  }, [active, stepIndex]);

  const value = useMemo<WalkthroughContextValue>(
    () => ({
      active,
      step,
      stepIndex,
      totalSteps: WALKTHROUGH_STEPS.length,
      isIdle,
      start,
      end,
      next,
    }),
    [active, step, stepIndex, isIdle, start, end, next]
  );

  return (
    <WalkthroughContext.Provider value={value}>
      {children}
    </WalkthroughContext.Provider>
  );
};

export const useWalkthrough = (): WalkthroughContextValue => {
  const ctx = useContext(WalkthroughContext);
  if (!ctx) {
    return {
      active: false,
      step: null,
      stepIndex: 0,
      totalSteps: 0,
      isIdle: false,
      start: () => {},
      end: () => {},
      next: () => {},
    };
  }
  return ctx;
};
